import { html, nothing } from "lit";
import type { CronJobEntry, TemplateSummary } from "../controllers/igloobot.ts";
import type { ChannelUiMetaEntry, CronJob, CronRunLogEntry, CronStatus } from "../types.ts";
import type { CronFormState } from "../ui-types.ts";
import { formatRelativeTimestamp, formatMs } from "../format.ts";
import { pathForTab } from "../navigation.ts";
import { formatCronSchedule, formatNextRun } from "../presenter.ts";

export type AgentOption = { id: string; name?: string };
export type ProjectOption = { id: string; name: string };

/** ISO string → value for a `datetime-local` input (local time, minute precision). */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` value → ISO string (or null when empty). */
function fromLocalInput(v: string): string | null {
  if (!v) {
    return null;
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export type CronProps = {
  basePath: string;
  loading: boolean;
  status: CronStatus | null;
  jobs: CronJob[];
  error: string | null;
  busy: boolean;
  form: CronFormState;
  channels: string[];
  channelLabels?: Record<string, string>;
  channelMeta?: ChannelUiMetaEntry[];
  runsJobId: string | null;
  runs: CronRunLogEntry[];
  // Project cron (merged view)
  projectCronJobs: CronJobEntry[];
  projectCronJobsLoading: boolean;
  /** cron_job templates for the create-form picklist. */
  cronTemplates?: TemplateSummary[];
  projects: ProjectOption[];
  agents: AgentOption[];
  projectFilter: string | null;
  agentFilter: string | null;
  // Project cron CRUD
  projectCronCreateMode: boolean;
  projectCronCreateForm: Partial<CronJobEntry> | null;
  projectCronEditingId: string | null;
  projectCronEditForm: Partial<CronJobEntry> | null;
  // Gateway cron callbacks
  onFormChange: (patch: Partial<CronFormState>) => void;
  onRefresh: () => void;
  onAdd: () => void;
  onToggle: (job: CronJob, enabled: boolean) => void;
  onRun: (job: CronJob) => void;
  onRemove: (job: CronJob) => void;
  onLoadRuns: (jobId: string) => void;
  // Filter callbacks
  onFilterProject: (projectId: string | null) => void;
  onFilterAgent: (agentId: string | null) => void;
  // Project cron callbacks
  onProjectCronRefresh: () => void;
  onProjectCronToggle: (id: string, active: boolean) => void;
  onProjectCronDelete: (id: string) => void;
  onProjectCronCreate: () => void;
  onProjectCronCancelCreate: () => void;
  onProjectCronFormChange: (field: string, value: unknown) => void;
  onProjectCronSave: () => void;
  onProjectCronEdit: (job: CronJobEntry) => void;
  onProjectCronCancelEdit: () => void;
  onProjectCronEditFormChange: (field: string, value: unknown) => void;
  onProjectCronSaveEdit: () => void;
};

export function renderCron(props: CronProps) {
  const selectedJob =
    props.runsJobId == null ? undefined : props.jobs.find((job) => job.id === props.runsJobId);
  const selectedRunTitle = selectedJob?.name ?? props.runsJobId ?? "(select a job)";
  const orderedRuns = props.runs.toSorted((a, b) => b.ts - a.ts);
  return html`
    <section class="card">
      <div class="row" style="justify-content:space-between;align-items:center;">
        <div>
          <div class="card-title">Cron scheduler</div>
          <div class="card-sub">
            ${props.status ? (props.status.enabled ? "Enabled" : "Disabled") : "n/a"} ·
            ${props.status?.jobs ?? 0} gateway jobs · next
            ${formatNextRun(props.status?.nextWakeAtMs ?? null)}
          </div>
        </div>
        <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
          ${props.loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      ${
        props.error
          ? html`<div class="callout danger" style="margin-top: 8px;">${props.error}</div>`
          : nothing
      }
    </section>

    ${renderProjectCronForm(props)}

    ${renderUnifiedCronTable(props)}

    <section class="card" style="margin-top: 18px;">
      <div class="card-title">Run history</div>
      <div class="card-sub">Latest runs for ${selectedRunTitle}.</div>
      ${
        props.runsJobId == null
          ? html`
              <div class="muted" style="margin-top: 12px">Select a job to inspect run history.</div>
            `
          : orderedRuns.length === 0
            ? html`
                <div class="muted" style="margin-top: 12px">No runs yet.</div>
              `
            : html`
              <div class="list" style="margin-top: 12px;">
                ${orderedRuns.map((entry) => renderRun(entry, props.basePath))}
              </div>
            `
      }
    </section>
  `;
}

// --------------- Project Cron Form ---------------

const TRIGGER_PAYLOAD_TEMPLATES: Record<string, Record<string, unknown>> = {
  create_task: { taskType: "reminder", message: "", targetUserId: "" },
  send_message: { channel: "telegram", message: "", recipientId: "" },
  run_skill: { skillName: "", parameters: {} },
};

function formatPayloadJson(payload: unknown): string {
  try {
    return JSON.stringify(payload ?? {}, null, 2);
  } catch {
    return "{}";
  }
}

const FORM_INPUT =
  "width:100%;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);";

/** Friendly schedule presets → 5-field cron expression. "" = custom (type your own). */
const FREQUENCY_PRESETS: Array<{ label: string; cron: string }> = [
  { label: "Custom (enter expression)", cron: "" },
  { label: "Every minute", cron: "* * * * *" },
  { label: "Hourly", cron: "0 * * * *" },
  { label: "Daily (09:00)", cron: "0 9 * * *" },
  { label: "Weekdays (Mon–Fri 09:00)", cron: "0 9 * * 1-5" },
  { label: "Weekly (Mon 09:00)", cron: "0 9 * * 1" },
  { label: "Monthly (1st, 09:00)", cron: "0 9 1 * *" },
];

/** Reverse-map a cron expression to its preset cron value (for <select> selection). */
function frequencyValueFor(cronExpression: string | undefined): string {
  const match = FREQUENCY_PRESETS.find((p) => p.cron !== "" && p.cron === (cronExpression ?? ""));
  return match ? match.cron : "";
}

function renderProjectCronFormFields(
  form: Partial<CronJobEntry>,
  isEdit: boolean,
  projects: ProjectOption[],
  onFormChange: (field: string, value: unknown) => void,
  onSave: () => void,
  onCancel: () => void,
  templates: TemplateSummary[] = [],
) {
  const payloadStr = formatPayloadJson(form.payload);
  const applyTemplate = (id: string) => {
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) {
      return;
    }
    const p = tpl.payload ?? {};
    if (!form.name) {
      onFormChange("name", tpl.description || tpl.name);
    }
    if (typeof p.cronExpression === "string") {
      onFormChange("cronExpression", p.cronExpression);
    }
    if (typeof p.trigger === "string") {
      onFormChange("trigger", p.trigger);
    }
    onFormChange("payload", p);
  };
  return html`
    <div style="display:grid;gap:10px;">
      ${
        !isEdit
          ? html`
            <div class="field">
              <label style="font-size:0.85em;">Project</label>
              <select
                style=${FORM_INPUT}
                @change=${(e: Event) => onFormChange("projectId", (e.target as HTMLSelectElement).value)}
              >
                ${
                  projects.length === 0
                    ? html`
                        <option value="">No projects available</option>
                      `
                    : projects.map(
                        (p) =>
                          html`<option value=${p.id} ?selected=${form.projectId === p.id}>${p.name}</option>`,
                      )
                }
              </select>
            </div>`
          : nothing
      }
      ${
        !isEdit && templates.length > 0
          ? html`
            <div class="field">
              <label style="font-size:0.85em;">Template (optional)</label>
              <select
                style=${FORM_INPUT}
                @change=${(e: Event) => applyTemplate((e.target as HTMLSelectElement).value)}
              >
                <option value="">— none —</option>
                ${templates.map((t) => html`<option value=${t.id}>${t.description || t.name}</option>`)}
              </select>
            </div>`
          : nothing
      }
      <div class="field">
        <label style="font-size:0.85em;">Name</label>
        <input type="text" .value=${form.name ?? ""}
          @input=${(e: Event) => onFormChange("name", (e.target as HTMLInputElement).value)}
          placeholder="Daily report" style=${FORM_INPUT} />
      </div>
      <div class="field">
        <label style="font-size:0.85em;">Frequency</label>
        <select
          style=${FORM_INPUT}
          .value=${frequencyValueFor(form.cronExpression)}
          @change=${(e: Event) => {
            const v = (e.target as HTMLSelectElement).value;
            if (v) {
              onFormChange("cronExpression", v);
            }
          }}
        >
          ${FREQUENCY_PRESETS.map(
            (p) =>
              html`<option value=${p.cron} ?selected=${p.cron === frequencyValueFor(form.cronExpression)}>${p.label}</option>`,
          )}
        </select>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
        <div class="field">
          <label style="font-size:0.85em;">Cron Expression</label>
          <input type="text" .value=${form.cronExpression ?? ""}
            @input=${(e: Event) => onFormChange("cronExpression", (e.target as HTMLInputElement).value)}
            placeholder="0 9 * * 1-5" style=${FORM_INPUT} />
        </div>
        <div class="field">
          <label style="font-size:0.85em;">Timezone</label>
          <input type="text" .value=${form.timezone ?? "UTC"}
            @input=${(e: Event) => onFormChange("timezone", (e.target as HTMLInputElement).value)}
            placeholder="UTC" style=${FORM_INPUT} />
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
        <div class="field">
          <label style="font-size:0.85em;">Start (optional)</label>
          <input type="datetime-local" .value=${toLocalInput(form.startAt)}
            @input=${(e: Event) =>
              onFormChange("startAt", fromLocalInput((e.target as HTMLInputElement).value))}
            style=${FORM_INPUT} />
        </div>
        <div class="field">
          <label style="font-size:0.85em;">Finish (optional)</label>
          <input type="datetime-local" .value=${toLocalInput(form.endAt)}
            @input=${(e: Event) =>
              onFormChange("endAt", fromLocalInput((e.target as HTMLInputElement).value))}
            style=${FORM_INPUT} />
        </div>
      </div>
      <div class="card-sub" style="font-size:0.8em;opacity:.7;">
        Job runs on its schedule only between Start and Finish. Leave blank for always-active.
      </div>
      <div class="field">
        <label style="font-size:0.85em;">Trigger</label>
        <div style="display:flex;gap:8px;align-items:center;">
          <select .value=${form.trigger ?? "create_task"}
            @change=${(e: Event) => {
              const trigger = (e.target as HTMLSelectElement).value;
              onFormChange("trigger", trigger);
              const current = form.payload;
              if (!current || Object.keys(current).length === 0) {
                const tmpl = TRIGGER_PAYLOAD_TEMPLATES[trigger];
                if (tmpl) {
                  onFormChange("payload", { ...tmpl });
                }
              }
            }}
            style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);flex:1;">
            <option value="create_task">Create Task</option>
            <option value="send_message">Send Message</option>
            <option value="run_skill">Run Skill</option>
          </select>
          <button class="btn btn--sm" @click=${() => {
            const tmpl = TRIGGER_PAYLOAD_TEMPLATES[form.trigger ?? "create_task"];
            if (tmpl) {
              onFormChange("payload", { ...tmpl });
            }
          }}>Load Template</button>
        </div>
      </div>
      <div class="field">
        <label style="font-size:0.85em;">Payload (JSON)</label>
        <textarea rows="4"
          .value=${payloadStr}
          @input=${(e: Event) => {
            const raw = (e.target as HTMLTextAreaElement).value;
            try {
              onFormChange("payload", JSON.parse(raw));
            } catch {
              /* ignore parse errors */
            }
          }}
          style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);font-family:var(--font-mono,monospace);font-size:0.85em;resize:vertical;"
          placeholder='{"key": "value"}'
        ></textarea>
      </div>
      <div style="display:flex;gap:8px;">
        <button class="btn btn--sm primary" @click=${onSave}>
          ${isEdit ? "Update" : "Create"}
        </button>
        <button class="btn btn--sm" @click=${onCancel}>Cancel</button>
      </div>
    </div>
  `;
}

function renderProjectCronForm(props: CronProps) {
  const {
    projectCronCreateMode,
    projectCronCreateForm,
    projectCronEditingId,
    projectCronEditForm,
  } = props;

  if (!projectCronCreateMode && !projectCronEditingId) {
    return html`
      <section class="card" style="margin-top: 18px;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div class="card-title">New Project Cron Job</div>
            <div class="card-sub">Create a DB-backed cron job for a project.</div>
          </div>
          <button class="btn primary" @click=${props.onProjectCronCreate}>
            + New Project Cron Job
          </button>
        </div>
      </section>
    `;
  }

  if (projectCronCreateMode && projectCronCreateForm) {
    return html`
      <section class="card" style="margin-top: 18px;border-color:var(--accent);">
        <div class="card-title">New Project Cron Job</div>
        <div class="card-sub" style="margin-bottom:12px;">Create a DB-backed cron job for a project.</div>
        ${renderProjectCronFormFields(
          projectCronCreateForm,
          false,
          props.projects,
          props.onProjectCronFormChange,
          props.onProjectCronSave,
          props.onProjectCronCancelCreate,
          props.cronTemplates ?? [],
        )}
      </section>
    `;
  }

  if (projectCronEditingId && projectCronEditForm) {
    return html`
      <section class="card" style="margin-top: 18px;border-color:var(--accent);">
        <div class="card-title">Edit Project Cron Job</div>
        <div class="card-sub" style="margin-bottom:12px;">Editing: ${projectCronEditForm.name ?? projectCronEditingId}</div>
        ${renderProjectCronFormFields(
          projectCronEditForm,
          true,
          props.projects,
          props.onProjectCronEditFormChange,
          props.onProjectCronSaveEdit,
          props.onProjectCronCancelEdit,
        )}
      </section>
    `;
  }

  return nothing;
}

// --------------- Unified Cron Table ---------------

type UnifiedCronRow = {
  id: string;
  source: "gateway" | "project";
  name: string;
  schedule: string;
  trigger: string;
  enabled: boolean;
  project?: string;
  agent?: string;
  nextRun: string;
  lastRun: string;
  status: string;
  // Original refs for actions
  gatewayJob?: CronJob;
  projectJob?: CronJobEntry;
};

function normalizeGatewayJob(job: CronJob): UnifiedCronRow {
  const schedule = formatCronSchedule(job);
  const trigger =
    job.payload.kind === "systemEvent"
      ? `system: ${(job.payload as { text?: string }).text ?? ""}`
      : `agent: ${(job.payload as { message?: string }).message ?? ""}`;
  return {
    id: `gw-${job.id}`,
    source: "gateway",
    name: job.name,
    schedule,
    trigger,
    enabled: job.enabled,
    agent: job.agentId ?? undefined,
    nextRun: formatNextRun(job.state?.nextRunAtMs ?? null),
    lastRun: job.state?.lastRunAtMs ? formatRelativeTimestamp(job.state.lastRunAtMs) : "n/a",
    status: job.state?.lastStatus ?? "n/a",
    gatewayJob: job,
  };
}

function normalizeProjectJob(job: CronJobEntry): UnifiedCronRow {
  return {
    id: `db-${job.id}`,
    source: "project",
    name: job.name,
    schedule: job.cronExpression + (job.timezone !== "UTC" ? ` (${job.timezone})` : ""),
    trigger: job.trigger.replace(/_/g, " "),
    enabled: job.isActive,
    project: job.projectName ?? job.projectId,
    nextRun: job.nextRunAt ? job.nextRunAt.split("T")[0] : "n/a",
    lastRun: job.lastRunAt ? job.lastRunAt.split("T")[0] : "n/a",
    status: job.isActive ? "active" : "inactive",
    projectJob: job,
  };
}

const SELECT_STYLE =
  "padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);";

function renderUnifiedCronTable(props: CronProps) {
  const gwRows = props.jobs.map(normalizeGatewayJob);
  const dbRows = props.projectCronJobs.map(normalizeProjectJob);
  const allRows = [...gwRows, ...dbRows];

  const isLoading = props.loading || props.projectCronJobsLoading;

  return html`
    <section class="card" style="margin-top: 18px;">
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;">
        <div>
          <div class="card-title">All Jobs</div>
          <div class="card-sub">
            Gateway scheduler and project cron jobs combined.
            <span class="muted">(${gwRows.length} gateway, ${dbRows.length} project)</span>
          </div>
        </div>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <button class="btn btn--sm" ?disabled=${isLoading} @click=${() => {
            props.onRefresh();
            props.onProjectCronRefresh();
          }}>
            ${isLoading ? "Loading..." : "Refresh All"}
          </button>

          <select style=${SELECT_STYLE}
            @change=${(e: Event) => {
              const v = (e.target as HTMLSelectElement).value;
              props.onFilterProject(v || null);
            }}
          >
            <option value="" ?selected=${!props.projectFilter}>All Projects</option>
            ${props.projects.map(
              (p) =>
                html`<option value=${p.id} ?selected=${props.projectFilter === p.id}>${p.name}</option>`,
            )}
          </select>

          <select style=${SELECT_STYLE}
            @change=${(e: Event) => {
              const v = (e.target as HTMLSelectElement).value;
              props.onFilterAgent(v || null);
            }}
          >
            <option value="" ?selected=${!props.agentFilter}>All Agents</option>
            ${props.agents.map(
              (a) =>
                html`<option value=${a.id} ?selected=${props.agentFilter === a.id}>${a.name ?? a.id}</option>`,
            )}
          </select>
        </div>
      </div>

      ${
        allRows.length === 0 && !isLoading
          ? html`
              <div class="muted" style="margin-top: 12px">No jobs found.</div>
            `
          : nothing
      }

      ${
        allRows.length > 0
          ? html`
              <div class="table" style="margin-top:16px;">
                <div class="table-head">
                  <span style="flex:0.6">Source</span>
                  <span style="flex:1.5">Name</span>
                  <span style="flex:1.5">Schedule</span>
                  <span style="flex:1.5">Trigger</span>
                  <span style="flex:0.6">Enabled</span>
                  <span style="flex:1">Project</span>
                  <span style="flex:0.8">Status</span>
                  <span style="flex:0.8">Next</span>
                  <span style="flex:1">Actions</span>
                </div>
                ${allRows.map(
                  (row) => html`
                  <div class="table-row" style="${row.gatewayJob && props.runsJobId === row.gatewayJob.id ? "background:var(--bg-inset);" : ""}">
                    <span style="flex:0.6">
                      <span class="pill pill--sm ${row.source === "gateway" ? "" : "pill--ok"}">${row.source}</span>
                    </span>
                    <span style="flex:1.5"><strong>${row.name}</strong></span>
                    <span style="flex:1.5" class="muted">${row.schedule}</span>
                    <span style="flex:1.5" class="muted" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${row.trigger}</span>
                    <span style="flex:0.6">
                      <span class="pill pill--sm ${row.enabled ? "pill--ok" : ""}">${row.enabled ? "yes" : "no"}</span>
                    </span>
                    <span style="flex:1">${row.project ?? row.agent ?? "—"}</span>
                    <span style="flex:0.8">
                      <span class="pill pill--sm ${row.status === "ok" || row.status === "active" ? "pill--ok" : row.status === "error" ? "pill--danger" : ""}">${row.status}</span>
                    </span>
                    <span style="flex:0.8" class="muted">${row.nextRun}</span>
                    <span style="flex:1">
                      ${
                        row.gatewayJob
                          ? html`
                        <button class="btn btn--sm" style="margin-right:4px;" ?disabled=${props.busy}
                          @click=${() => props.onToggle(row.gatewayJob!, !row.gatewayJob!.enabled)}>
                          ${row.gatewayJob!.enabled ? "Disable" : "Enable"}
                        </button>
                        <button class="btn btn--sm" style="margin-right:4px;" ?disabled=${props.busy}
                          @click=${() => props.onRun(row.gatewayJob!)}>Run</button>
                        <button class="btn btn--sm" style="margin-right:4px;"
                          @click=${() => props.onLoadRuns(row.gatewayJob!.id)}>History</button>
                        <button class="btn btn--sm danger" ?disabled=${props.busy}
                          @click=${() => props.onRemove(row.gatewayJob!)}>Remove</button>
                      `
                          : nothing
                      }
                      ${
                        row.projectJob
                          ? html`
                        <button class="btn btn--sm" style="margin-right:4px;"
                          @click=${() => props.onProjectCronToggle(row.projectJob!.id, !row.projectJob!.isActive)}>
                          ${row.projectJob!.isActive ? "Disable" : "Enable"}
                        </button>
                        <button class="btn btn--sm" style="margin-right:4px;"
                          @click=${() => props.onProjectCronEdit(row.projectJob!)}>Edit</button>
                        <button class="btn btn--sm danger"
                          @click=${() => props.onProjectCronDelete(row.projectJob!.id)}>Delete</button>
                      `
                          : nothing
                      }
                    </span>
                  </div>
                `,
                )}
              </div>
            `
          : nothing
      }
    </section>
  `;
}

function renderJob(job: CronJob, props: CronProps) {
  const isSelected = props.runsJobId === job.id;
  const itemClass = `list-item list-item-clickable cron-job${isSelected ? " list-item-selected" : ""}`;
  return html`
    <div class=${itemClass} @click=${() => props.onLoadRuns(job.id)}>
      <div class="list-main">
        <div class="list-title">${job.name}</div>
        <div class="list-sub">${formatCronSchedule(job)}</div>
        ${renderJobPayload(job)}
        ${job.agentId ? html`<div class="muted cron-job-agent">Agent: ${job.agentId}</div>` : nothing}
      </div>
      <div class="list-meta">
        ${renderJobState(job)}
      </div>
      <div class="cron-job-footer">
        <div class="chip-row cron-job-chips">
          <span class=${`chip ${job.enabled ? "chip-ok" : "chip-danger"}`}>
            ${job.enabled ? "enabled" : "disabled"}
          </span>
          <span class="chip">${job.sessionTarget}</span>
          <span class="chip">${job.wakeMode}</span>
        </div>
        <div class="row cron-job-actions">
          <button
            class="btn"
            ?disabled=${props.busy}
            @click=${(event: Event) => {
              event.stopPropagation();
              props.onToggle(job, !job.enabled);
            }}
          >
            ${job.enabled ? "Disable" : "Enable"}
          </button>
          <button
            class="btn"
            ?disabled=${props.busy}
            @click=${(event: Event) => {
              event.stopPropagation();
              props.onRun(job);
            }}
          >
            Run
          </button>
          <button
            class="btn"
            ?disabled=${props.busy}
            @click=${(event: Event) => {
              event.stopPropagation();
              props.onLoadRuns(job.id);
            }}
          >
            History
          </button>
          <button
            class="btn danger"
            ?disabled=${props.busy}
            @click=${(event: Event) => {
              event.stopPropagation();
              props.onRemove(job);
            }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  `;
}

function renderJobPayload(job: CronJob) {
  if (job.payload.kind === "systemEvent") {
    return html`<div class="cron-job-detail">
      <span class="cron-job-detail-label">System</span>
      <span class="muted cron-job-detail-value">${job.payload.text}</span>
    </div>`;
  }

  const delivery = job.delivery;
  const deliveryTarget =
    delivery?.channel || delivery?.to
      ? ` (${delivery.channel ?? "last"}${delivery.to ? ` -> ${delivery.to}` : ""})`
      : "";

  return html`
    <div class="cron-job-detail">
      <span class="cron-job-detail-label">Prompt</span>
      <span class="muted cron-job-detail-value">${job.payload.message}</span>
    </div>
    ${
      delivery
        ? html`<div class="cron-job-detail">
            <span class="cron-job-detail-label">Delivery</span>
            <span class="muted cron-job-detail-value">${delivery.mode}${deliveryTarget}</span>
          </div>`
        : nothing
    }
  `;
}

function formatStateRelative(ms?: number) {
  if (typeof ms !== "number" || !Number.isFinite(ms)) {
    return "n/a";
  }
  return formatRelativeTimestamp(ms);
}

function renderJobState(job: CronJob) {
  const status = job.state?.lastStatus ?? "n/a";
  const statusClass =
    status === "ok"
      ? "cron-job-status-ok"
      : status === "error"
        ? "cron-job-status-error"
        : status === "skipped"
          ? "cron-job-status-skipped"
          : "cron-job-status-na";
  const nextRunAtMs = job.state?.nextRunAtMs;
  const lastRunAtMs = job.state?.lastRunAtMs;

  return html`
    <div class="cron-job-state">
      <div class="cron-job-state-row">
        <span class="cron-job-state-key">Status</span>
        <span class=${`cron-job-status-pill ${statusClass}`}>${status}</span>
      </div>
      <div class="cron-job-state-row">
        <span class="cron-job-state-key">Next</span>
        <span class="cron-job-state-value" title=${formatMs(nextRunAtMs)}>
          ${formatStateRelative(nextRunAtMs)}
        </span>
      </div>
      <div class="cron-job-state-row">
        <span class="cron-job-state-key">Last</span>
        <span class="cron-job-state-value" title=${formatMs(lastRunAtMs)}>
          ${formatStateRelative(lastRunAtMs)}
        </span>
      </div>
    </div>
  `;
}

function renderRun(entry: CronRunLogEntry, basePath: string) {
  const chatUrl =
    typeof entry.sessionKey === "string" && entry.sessionKey.trim().length > 0
      ? `${pathForTab("chat", basePath)}?session=${encodeURIComponent(entry.sessionKey)}`
      : null;
  return html`
    <div class="list-item">
      <div class="list-main">
        <div class="list-title">${entry.status}</div>
        <div class="list-sub">${entry.summary ?? ""}</div>
      </div>
      <div class="list-meta">
        <div>${formatMs(entry.ts)}</div>
        <div class="muted">${entry.durationMs ?? 0}ms</div>
        ${
          chatUrl
            ? html`<div><a class="session-link" href=${chatUrl}>Open run chat</a></div>`
            : nothing
        }
        ${entry.error ? html`<div class="muted">${entry.error}</div>` : nothing}
      </div>
    </div>
  `;
}
